/**
 * Clicking a face - or a game - anywhere.
 *
 * Avatars in a conversation opened a profile; the same avatar in the
 * leaderboard, a party roster or a crew page did nothing at all, which reads
 * as broken rather than as a deliberate difference. Every avatar now carries
 * the username it belongs to and this one listener turns that into a click.
 *
 * Delegated rather than wired per avatar for two reasons: there is one
 * listener instead of several hundred handlers, and a picture inside something
 * already interactive keeps belonging to that thing. Clicking the face on a
 * conversation row opens the conversation, exactly as before.
 */

import { openProfile } from '@/components/ProfilePanel';
import { isShowing, openUserCard } from '@/components/user';
import { userOfFace } from '@/components/Avatar';
import type { PublicUser } from './types';
import { go } from './hash';

/** Things that own their own clicks, and so are left alone. */
const OWNED = 'button, a, [role="button"], [role="menuitem"], input, textarea, select, label, [data-no-profile]';

function onClick(e: MouseEvent): void {
  // Modified clicks are for the browser, and a drag is not a click.
  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

  const target = e.target;
  if (!(target instanceof Element)) return;

  const face = target.closest('[data-profile], [data-game]');
  if (!face) return;

  /* Only when nothing between the picture and the page already handles
     clicks - and only when that thing is inside the picture's own subtree,
     so an avatar sitting inside a card still works while an avatar that is
     part of a button does not steal the button's click. */
  const owner = target.closest(OWNED);
  if (owner && !face.contains(owner)) return;
  if (owner && owner !== face && face.contains(owner)) return;

  const username = face.getAttribute('data-profile');
  const game = face.getAttribute('data-game');
  if (!username && !game) return;

  e.preventDefault();
  e.stopPropagation();
  /*
   * The small card, not the whole screen.
   *
   * Clicking a face is a glance - who is this, are they online, do we have
   * friends in common. Taking over the window for that is the wrong size of
   * answer, and the card has a way through to the full profile for when it is
   * not enough. The panel is still what a direct link to a profile opens.
   */
  if (username) {
    /* Their own card or full profile is already open around this picture. */
    if (isShowing(username, face)) return;
    const known = face instanceof HTMLElement ? userOfFace(face) : null;
    if (known && face instanceof HTMLElement) openUserCard(known as PublicUser, face);
    else openProfile(username);
  }
  else if (game) go('parties?game=' + encodeURIComponent(game));
}

/** Starts listening. Called once, from the shell. */
export function watchProfileClicks(): () => void {
  document.addEventListener('click', onClick);
  return () => document.removeEventListener('click', onClick);
}
